import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Avatar,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  CircularProgress,
  FormControlLabel,
  styled,
  Stack,
  Switch,
  Tooltip,
} from '@mui/material'
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

import { BankService } from '@/api/services/BankService'
import { connectionQueryKeys } from '@/api/services/connectionQueryKeys'
import { replaceNullValues } from '@/utils/common'
import { IBankConnection } from '@/types'
import SearchLogo from '@/assets/search.svg'
import { Accordion } from '@/components/UI/Accordion'
import { Chip } from '@/components/UI/Chip'
import { ConnectionDetail } from '@/components/ConncectionDetail'
import { FinancialAccountsList } from '@/components/FinancialAccounts'
import { useFinancialAccounts } from '@/components/FinancialAccounts/useFinancialAccounts'
import { DeleteConnection } from '@/components/DeleteConnection'
import { ReloadConnections } from '@/components/ReloadConnections'
import { useAppStore } from '@/store'
import { ErrorPage } from '../Error'
import { orderConnections } from './mainConnection'
import { groupConnections } from './connectionGroups'
import { shownInstitutionStatus } from './institutionStatus'

// The grouped view is a prototype under comparison, so the choice is remembered per browser to
// keep a reviewer on the view they picked. Storage can be unavailable, in which case it is not.
const GROUPED_VIEW_KEY = 'connections.groupedView'

const readGroupedView = () => {
  try {
    return localStorage.getItem(GROUPED_VIEW_KEY) === 'true'
  } catch {
    return false
  }
}

export const Connections: React.FC = () => {
  const [expandedPanels, setExpandedPanels] = useState<Set<string>>(new Set())
  const [isGrouped, setGroupedState] = useState(readGroupedView)
  const setIsGrouped = (grouped: boolean) => {
    setGroupedState(grouped)
    // The list keeps one panel open at a time, so leaving the grouped view keeps only the panel
    // opened last (a set keeps the order its entries were added in).
    if (!grouped) {
      setExpandedPanels(open => new Set([...open].slice(-1)))
    }
    try {
      localStorage.setItem(GROUPED_VIEW_KEY, String(grouped))
    } catch {
      // Not remembered, but the toggle still works for this visit.
    }
  }
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  // The customer this tab shows. Everything below reads and writes for them alone.
  const userId = useAppStore(state => state.user?.id) ?? ''

  const {
    data: connections,
    isLoading,
    isError,
  } = useQuery<IBankConnection[]>({
    queryKey: connectionQueryKeys.list(userId),
    queryFn: () => BankService.fetchConnections(userId),
    enabled: !!userId,
  })

  // The list keeps one panel open at a time. The grouped view lets several stay open, so accounts
  // under different connections can be compared side by side.
  const handleAccordionChange =
    (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedPanels(open => {
        const next = new Set(isGrouped ? open : [])
        if (isExpanded) {
          next.add(panel)
        } else {
          next.delete(panel)
        }
        return next
      })
    }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={5}>
        <CircularProgress size={25} sx={{ color: 'lightgray' }} />
      </Box>
    )
  }

  if (isError) {
    return (
      <ErrorPage
        title={'Unable to Fetch Financial Accounts'}
        message={
          "We couldn't retrieve customer data at the moment. Please check your internet connection or try again later.If the issue persists, contact administrator."
        }
      />
    )
  }

  if (!connections || connections.length === 0) {
    return (
      <EmptyContainer>
        <Box component="img" src={SearchLogo} alt="Search Icon" mb={2} />
        <Title>No Accounts Found</Title>
        <Message>The customer hasn't added any accounts yet</Message>
        <Box mt={2}>
          <ReloadConnections userId={userId} />
        </Box>
      </EmptyContainer>
    )
  }

  const { ordered: transformedConnections, mainId } = orderConnections(connections)
  const groups = groupConnections(connections)

  const renderCard = (connection: IBankConnection, showMainLabel: boolean, showAccounts = true) => {
    const safeConnection = replaceNullValues<IBankConnection>(connection)
    const institutionStatus = shownInstitutionStatus(safeConnection)
    return (
      <Box
        key={safeConnection.id}
        p={2}
        sx={{ border: '1px solid #E4E7EC', borderRadius: '8px', backgroundColor: '#FFFFFF' }}
      >
        <Box pb={1}>
          {showMainLabel && (
            <Typography
              sx={{ fontFamily: 'Inter', fontSize: '12px', fontWeight: 'bold' }}
              variant="body2"
              pb={1}
            >
              MAIN
            </Typography>
          )}
          <Box
            display="flex"
            flexDirection={isMobile ? 'column' : 'row'}
            justifyContent="space-between"
          >
            <Box mb={1} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Avatar
                sx={{ backgroundColor: '#F6F8F9', width: 28, height: 28 }}
                src={safeConnection.squareLogoUrl}
              >
                <AccountBalanceOutlinedIcon fontSize="small" sx={{ color: '#344054' }} />
              </Avatar>
              <Typography sx={{ fontSize: '16px', fontWeight: '600' }}>
                {safeConnection.name}
              </Typography>
              {institutionStatus && <Chip value={institutionStatus} />}
            </Box>
            <DeleteConnection
              userId={userId}
              connectionId={safeConnection.id}
              connectionName={safeConnection.name}
              connectionStatus={safeConnection.status}
            />
          </Box>
        </Box>

        <Stack direction={'column'} gap={2}>
          <ConnectionDetail userId={userId} connectionId={safeConnection.id} />

          {showAccounts && (
            <Accordion
              summary={
                <FinancialAccountsSummary
                  userId={userId}
                  connectionId={safeConnection.id}
                  isPrimary={!!connection.isPrimary}
                />
              }
              content={<FinancialAccountsList userId={userId} connectionId={safeConnection.id} />}
              expanded={expandedPanels.has(`panel2-${safeConnection.id}`)}
              onChange={handleAccordionChange(`panel2-${safeConnection.id}`)}
            />
          )}
        </Stack>
      </Box>
    )
  }

  // A group with nothing in it is left out rather than shown empty.
  // Each group sits in a tinted panel with an accent down its side, so where one group ends and the
  // next begins reads at a glance rather than from the headings alone.
  const renderGroup = (
    title: string,
    description: string,
    members: IBankConnection[],
    accent: string,
    showAccounts = true
  ) =>
    members.length > 0 && (
      <GroupPanel component="section" aria-label={title} sx={{ borderLeftColor: accent }}>
        <GroupTitle>
          {title} <CountBadge>{members.length}</CountBadge>
          {/* MUI hides its icons from assistive technology, so the label and focus sit on a wrapper
              that screen readers can reach. titleAccess would do it too, but adds the browser's own
              tooltip on top of this one. */}
          <Tooltip title={description} placement="right" arrow>
            <InfoTrigger role="img" tabIndex={0} aria-label={description}>
              <InfoOutlinedIcon sx={{ fontSize: 16, color: '#667085' }} />
            </InfoTrigger>
          </Tooltip>
        </GroupTitle>
        <Stack spacing={2}>
          {members.map(connection => renderCard(connection, false, showAccounts))}
        </Stack>
      </GroupPanel>
    )

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        pb={2}
      >
        <Typography variant="h6">Connections & Accounts</Typography>
        <Box display="flex" alignItems="center" gap={2}>
          {/* Prototype: compares grouping connections by health against the plain list. */}
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={isGrouped}
                onChange={event => setIsGrouped(event.target.checked)}
              />
            }
            label={<ToggleLabel>Grouped view</ToggleLabel>}
          />
          <ReloadConnections userId={userId} />
        </Box>
      </Box>

      {isGrouped ? (
        <Stack spacing={4}>
          {renderGroup(
            'Primary connection',
            'The primary connection, while it is active, near expiry or pending. With more than one, the most recently synced.',
            groups.main ? [groups.main] : [],
            GROUP_ACCENT.primary
          )}
          {renderGroup(
            'Other active connections',
            'Every other connection that is still live or on its way there, such as pending.',
            groups.other,
            GROUP_ACCENT.other
          )}
          {/* An inactive connection can no longer be read from the bank, so it has no accounts to show. */}
          {renderGroup(
            'Inactive connections',
            'Inactive, revoked or expired connections. These can no longer sync, so their accounts cannot be fetched.',
            groups.inactive,
            GROUP_ACCENT.inactive,
            false
          )}
        </Stack>
      ) : (
        <Stack spacing={2}>
          {transformedConnections.map(connection =>
            renderCard(connection, connection.id === mainId)
          )}
        </Stack>
      )}
    </Box>
  )
}

// The count and the salary pill come from the same read as the list inside, so showing them costs
// no extra request. Both are left off until the accounts are in, rather than reading as zero.
// A salary account is flagged wherever there is one; its absence only on the primary connection,
// which is where one is expected.
const FinancialAccountsSummary: React.FC<{
  userId: string
  connectionId: string
  isPrimary: boolean
}> = ({ userId, connectionId, isPrimary }) => {
  const { data: accounts } = useFinancialAccounts(userId, connectionId)
  const hasSalary = accounts?.some(account => account.isSalary)

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <StyledTypography>Financial Accounts</StyledTypography>
      {accounts && <CountBadge>{accounts.length}</CountBadge>}
      {accounts && hasSalary && <Chip value="Salary account detected" chipColor="#067647" />}
      {accounts && !hasSalary && isPrimary && (
        <Chip value="No salary account detected" chipColor="#B54708" />
      )}
    </Box>
  )
}

const StyledTypography = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontFamily: 'Inter',
  fontWeight: 600,
  color: '#344054',
  textTransform: 'capitalize',
  marginLeft: '10px',
  [theme.breakpoints.down('md')]: {
    marginLeft: '0px',
  },
}))

const EmptyContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
})

const Title = styled(Typography)(({ theme }) => ({
  ...theme.typography.h6,
  fontSize: '0.9rem',
  paddingBottom: theme.spacing(3),
}))

const Message = styled(Typography)(({ theme }) => ({
  ...theme.typography.body1,
  color: theme.palette.text.secondary,
  fontSize: '0.9rem',
  fontWeight: 500,
  maxWidth: 600,
}))

// Brand purple for the primary connection, green for the others still live, and grey for the
// inactive ones -- expected rather than an error, so not red.
const GROUP_ACCENT = {
  primary: '#2C063F',
  other: '#067647',
  inactive: '#98A2B3',
}

const GroupPanel = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  border: '1px solid #EAECF0',
  borderLeftWidth: 4,
  borderRadius: '12px',
  backgroundColor: '#F9FAFB',
})) as typeof Box

const GroupTitle = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  fontFamily: 'Inter',
  fontSize: '16px',
  fontWeight: 600,
  color: '#101828',
  paddingBottom: theme.spacing(2),
}))

const CountBadge = styled('span')({
  minWidth: 20,
  padding: '0 6px',
  borderRadius: '999px',
  backgroundColor: '#F2F4F7',
  fontSize: '12px',
  fontWeight: 500,
  lineHeight: '20px',
  textAlign: 'center',
})

const InfoTrigger = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  cursor: 'help',
  borderRadius: '50%',
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
}))

const ToggleLabel = styled(Typography)({
  fontFamily: 'Inter',
  fontSize: '14px',
  fontWeight: 500,
  color: '#344054',
})
